import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Link as MuiLink,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { downloadApiFile } from '../../api/download';
import { getFileDetail } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import { CopyableText } from '../../components/CopyableText';
import { ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { FileKindChip, OperationStatusChip } from '../../components/StatusChips';
import { fileRoleLabelKeys, fileSourceLabelKeys } from '../../components/statusLabels';
import { useErrorToast } from '../../hooks/useErrorToast';
import { useSelectedTenant } from '../../tenant/tenantSelection';
import { formatBytes, formatDateTime } from '../../utils/format';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {typeof children === 'string' ? <Typography variant="body2">{children}</Typography> : children}
    </Box>
  );
}

export interface FileDetailDrawerProps {
  fileGid: string | null;
  onClose: () => void;
}

export function FileDetailDrawer({ fileGid, onClose }: FileDetailDrawerProps) {
  const { t } = useTranslation();
  const toastError = useErrorToast();
  const { selectedTenantGid } = useSelectedTenant();

  // A tenant-less query reaches the fallback schema and fails instead of returning an empty detail.
  const query = useQuery({
    queryKey: queryKeys.files.detail(selectedTenantGid, fileGid ?? ''),
    queryFn: ({ signal }) => getFileDetail(fileGid ?? '', signal),
    enabled: fileGid !== null && selectedTenantGid !== null,
  });

  const detail = query.data;

  const download = async () => {
    if (!detail) return;
    try {
      await downloadApiFile(`/file/${detail.file.fileGid}`, { fallbackFileName: detail.file.fileName });
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <Drawer anchor="right" open={fileGid !== null} onClose={onClose}>
      {/* A column with pinned header and footer: however long the detail grows, a close control
          stays on screen — the body alone scrolls. */}
      <Box sx={{ width: { xs: '100vw', sm: 460 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography variant="h6">{t('files.detail.title')}</Typography>
          <IconButton onClick={onClose} aria-label={t('actions.close')}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2.5 }}>
          {query.isPending && <LoadingBlock />}
          {query.isError && <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />}

          {detail && (
            <Stack spacing={2}>
              <Field label={t('files.detail.fileName')}>{detail.file.fileName}</Field>

              <Stack direction="row" spacing={1} alignItems="center">
                <FileKindChip kind={detail.file.kind} />
                <Chip size="small" label={t(fileSourceLabelKeys[detail.file.source])} variant="outlined" />
                {detail.file.deduplicated && (
                  <Chip size="small" label={t('files.detail.deduplicatedChip')} color="info" />
                )}
              </Stack>

              <Field label={t('files.detail.identifier')}>
                <CopyableText value={detail.file.fileGid} />
              </Field>
              <Field label={t('files.detail.mediaType')}>{detail.file.mimeType}</Field>
              <Field label={t('files.detail.size')}>{formatBytes(detail.file.sizeBytes)}</Field>
              <Field label="SHA-256">
                {detail.file.sha256 ? <CopyableText value={detail.file.sha256} /> : '—'}
              </Field>
              <Field label={t('files.detail.storageProvider')}>{detail.storageProvider}</Field>
              <Field label={t('files.detail.created')}>{formatDateTime(detail.createdAt)}</Field>
              <Field label={t('files.detail.lastModified')}>{formatDateTime(detail.lastModifiedAt)}</Field>

              <Button startIcon={<DownloadIcon />} variant="outlined" onClick={() => void download()}>
                {t('actions.download')}
              </Button>

              <Divider />

              <Typography variant="subtitle2">
                {t('files.detail.referencedBy', {
                  count: detail.operations.length + detail.receivedDocumentGids.length,
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('files.detail.referencedByNote')}
              </Typography>

              {detail.operations.length === 0 && detail.receivedDocumentGids.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('files.detail.noReferences')}
                </Typography>
              )}

              {detail.operations.length > 0 && (
                <List dense disablePadding>
                  {detail.operations.map((reference) => (
                    <ListItem key={`${reference.operationGid}-${reference.role}`} disableGutters>
                      <ListItemText
                        primary={
                          <MuiLink component={RouterLink} to={`/operations/${reference.operationGid}`}>
                            {t(fileRoleLabelKeys[reference.role])}
                          </MuiLink>
                        }
                        secondary={reference.operationGid}
                      />
                      <OperationStatusChip status={reference.status} />
                    </ListItem>
                  ))}
                </List>
              )}

              {detail.receivedDocumentGids.length > 0 && (
                <>
                  <Typography variant="subtitle2">{t('files.detail.receivedDocuments')}</Typography>
                  <List dense disablePadding>
                    {detail.receivedDocumentGids.map((gid) => (
                      <ListItem key={gid} disableGutters>
                        <ListItemText secondary={gid} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Stack>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            px: 2.5,
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Button variant="outlined" onClick={onClose}>
            {t('actions.close')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
